/**
 * Per-history-entry snapshot of the result view (response + scroll position).
 *
 * Saved when the user leaves the result view and restored on Back/Forward or
 * reload, so returning shows the same results at the same scroll position
 * without a new search. Stored in sessionStorage (per tab) and kept only for
 * a few hours because the search index is updated frequently.
 */

import type { SearchResponse } from '../types/search';

export const SNAPSHOT_STORAGE_KEY = 'enterprise-search:result-snapshots';
export const SNAPSHOT_TTL_MS = 2 * 60 * 60 * 1000;
export const SNAPSHOT_MAX_ENTRIES = 10;

export interface SearchSnapshot {
  /** Canonical search params (see `buildSearchParams`) the response belongs to. */
  url: string;
  /** User ID or `anonymous`; results depend on the caller's ACL. */
  identity: string;
  /** Content language the search ran with. */
  language: string;
  response: SearchResponse;
  scrollY: number;
  /**
   * First result visible when leaving and its distance from the viewport top.
   * Restoring relative to it is unaffected by layout changes above the results.
   */
  anchor?: { resultId: string; top: number };
  savedAt: number;
}

export interface SnapshotMatch {
  url: string;
  identity: string;
  language: string;
}

type SnapshotMap = Record<string, SearchSnapshot>;

function defaultStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function isFresh(snapshot: SearchSnapshot, now: number): boolean {
  const age = now - snapshot.savedAt;
  return age >= 0 && age < SNAPSHOT_TTL_MS;
}

function readMap(storage: Storage): SnapshotMap {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(SNAPSHOT_STORAGE_KEY) ?? '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as SnapshotMap)
      : {};
  } catch {
    return {};
  }
}

function writeMap(storage: Storage, map: SnapshotMap): boolean {
  try {
    if (Object.keys(map).length === 0) storage.removeItem(SNAPSHOT_STORAGE_KEY);
    else storage.setItem(SNAPSHOT_STORAGE_KEY, JSON.stringify(map));
    return true;
  } catch {
    return false;
  }
}

/** Store a snapshot under a history entry key, pruning expired and excess entries. */
export function saveSnapshot(
  historyKey: string,
  snapshot: SearchSnapshot,
  storage: Storage | null = defaultStorage()
): void {
  if (!storage) return;
  const entries = Object.entries(readMap(storage)).filter(
    ([key, entry]) => key !== historyKey && entry?.savedAt && isFresh(entry, snapshot.savedAt)
  );
  entries.push([historyKey, snapshot]);
  entries.sort(([, a], [, b]) => b.savedAt - a.savedAt);
  const map = Object.fromEntries(entries.slice(0, SNAPSHOT_MAX_ENTRIES));
  // Quota exceeded: keep only the newest snapshot rather than none.
  if (!writeMap(storage, map)) writeMap(storage, { [historyKey]: snapshot });
}

/** Snapshot for a history entry, or `null` if missing, expired or not matching the current state. */
export function loadSnapshot(
  historyKey: string,
  match: SnapshotMatch,
  storage: Storage | null = defaultStorage(),
  now: number = Date.now()
): SearchSnapshot | null {
  if (!storage) return null;
  const snapshot = readMap(storage)[historyKey];
  if (
    !snapshot ||
    !isFresh(snapshot, now) ||
    snapshot.url !== match.url ||
    snapshot.identity !== match.identity ||
    snapshot.language !== match.language ||
    !snapshot.response ||
    !Array.isArray(snapshot.response.results)
  ) {
    return null;
  }
  return snapshot;
}
